import { useState } from 'react'
import { MessageSquare, Send } from 'lucide-react'
import { fetchWithAuth } from '@/lib/fetchWithAuth'
import type { InputRequestBlock } from '@/types/blocks'

interface InlineInputRequestProps {
  block: InputRequestBlock
}

/**
 * HITL input request card rendered inline in chat.
 * Supports: text input, choice selection, or yes/no confirmation.
 * After submission, becomes read-only showing the response.
 */
export function InlineInputRequest({ block }: InlineInputRequestProps) {
  const { request_id, prompt, input_type, options, default: defaultValue } = block.data
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [textValue, setTextValue] = useState(defaultValue || '')
  const [selectedOption, setSelectedOption] = useState<string>(defaultValue || '')
  const [submittedValue, setSubmittedValue] = useState('')

  const submit = async (value: string) => {
    setLoading(true)
    try {
      await fetchWithAuth(`/api/approvals/${request_id}/modify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modified_action: { user_input: value },
          reason: value,
        }),
      })
      setSubmittedValue(value)
      setSubmitted(true)
    } catch (err) {
      console.error('Input submission failed:', err)
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
        <div className="flex items-center gap-2 text-sm">
          <MessageSquare className="h-4 w-4 text-primary" />
          <span className="font-medium">Response submitted:</span>
          <span className="text-muted-foreground">{submittedValue}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
      <div className="flex items-start gap-2">
        <MessageSquare className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
        <p className="text-sm font-medium text-foreground">{prompt}</p>
      </div>

      {input_type === 'text' && (
        <div className="flex gap-2">
          <input
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && textValue.trim() && submit(textValue.trim())}
            className="flex-1 text-sm bg-background border rounded-md px-3 py-1.5"
            placeholder="Type your response..."
            disabled={loading}
          />
          <button
            onClick={() => submit(textValue.trim())}
            disabled={loading || !textValue.trim()}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            <Send className="h-3.5 w-3.5" />
            Submit
          </button>
        </div>
      )}

      {input_type === 'choice' && options && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-2">
            {options.map((option) => (
              <button
                key={option}
                onClick={() => setSelectedOption(option)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                  selectedOption === option
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background border-border hover:bg-muted'
                }`}
                disabled={loading}
              >
                {option}
              </button>
            ))}
          </div>
          {selectedOption && (
            <button
              onClick={() => submit(selectedOption)}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              <Send className="h-3.5 w-3.5" />
              Confirm Selection
            </button>
          )}
        </div>
      )}

      {input_type === 'confirmation' && (
        <div className="flex gap-2">
          <button
            onClick={() => submit('yes')}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
          >
            Yes
          </button>
          <button
            onClick={() => submit('no')}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-md text-xs font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            No
          </button>
        </div>
      )}
    </div>
  )
}
